import {Order} from '../server/functions/orders/orders.types.js'

type OrderAddress = Order['addresses'][0]
type HistoryCollo = NonNullable<Order['history'][0]['colli']>[0]

/**
 * Whether the order's addresses are the retour address of a retour. A retour replaces the order's addresses with the
 * retour address and keeps the original addresses in originalAddresses (see sendRetour). The partner's copy of a
 * returned order keeps its own addresses, its retourId points at the sender's order.
 * LEGACY: a separate retour order has no retourId of its own and only the retour address.
 */
export function isRetourOrder(order: Pick<Order, 'type' | 'retourId' | 'originalAddresses'>): boolean {
    return order.type === 'retour' && (!!order.originalAddresses?.length || !order.retourId)
}

/**
 * The (at most) two addresses a list shows for an order: its first and last address. For a retour these are the first
 * original address (no longer part of the order, so shown greyed out) and the retour address.
 */
export function getListAddresses(order: Pick<Order, 'type' | 'retourId' | 'addresses' | 'originalAddresses'>): {firstAddress?: OrderAddress, lastAddress?: OrderAddress, firstIsOriginal: boolean, retourAddress?: OrderAddress, nrOfOtherAddresses: number} {
    if (isRetourOrder(order)) {
        const retourAddress = order.addresses[order.addresses.length - 1]
        const originalAddress = order.originalAddresses?.[0]

        return {
            firstAddress: originalAddress || retourAddress,
            lastAddress: originalAddress ? retourAddress : undefined,
            firstIsOriginal: !!originalAddress,
            retourAddress,
            nrOfOtherAddresses: Math.max((order.originalAddresses?.length || 0) - 1, 0)
        }
    }

    return {
        firstAddress: order.addresses[0],
        lastAddress: order.addresses.length > 1 ? order.addresses[order.addresses.length - 1] : undefined,
        firstIsOriginal: false,
        nrOfOtherAddresses: Math.max(order.addresses.length - 2, 0)
    }
}

/**
 * The addresses the order was created with: for a retour its original addresses, e.g. to copy the order.
 */
export function getOriginalOrderAddresses(order: Pick<Order, 'type' | 'retourId' | 'addresses' | 'originalAddresses'>): OrderAddress[] {
    return isRetourOrder(order) && order.originalAddresses?.length ? order.originalAddresses : order.addresses
}

/**
 * Description of the history entry sendRetour adds when the order goes retour.
 */
export const retourHistoryDescription = 'Retour afzender'

/**
 * The colli of a retour as they were just before the order went retour, by collo index. Empty when the order has no
 * retour entry in its history.
 */
function getColliBeforeRetour(order: Pick<Order, 'history'>): Record<number, HistoryCollo> {
    const history = order.history || []
    let retourEntryIndex = -1

    // The last one: a retour can be cancelled and sent again
    for (let i = history.length - 1; i >= 0; i--) {
        if (history[i].description === retourHistoryDescription) {
            retourEntryIndex = i
            break
        }
    }

    // A history entry can hold only the colli it changed, so the entries before the retour are merged by collo index
    const colliByIndex: Record<number, HistoryCollo> = {}

    if (retourEntryIndex === -1) {
        return colliByIndex
    }

    history.slice(0, retourEntryIndex).forEach((entry) => {
        entry.colli?.forEach((collo) => {
            colliByIndex[collo.index] = collo
        })
    })

    return colliByIndex
}

/**
 * The colli of an original address of a retour as they were just before the order went retour, to show the status the
 * address ended with. The order's colli now belong to the retour address, so the state is taken from the history.
 */
export function getOriginalAddressColli(order: Pick<Order, 'history'>, address: OrderAddress): HistoryCollo[] {
    return Object.values(getColliBeforeRetour(order)).filter((collo) => {
        return (address.type === 'pickup' && collo.pickupAddressIndex === address.index) || (address.type === 'delivery' && collo.deliveryAddressIndex === address.index)
    })
}

/**
 * The delivery address (an index in originalAddresses) a returned collo of a retour had before the order went retour,
 * sendRetour moves it to the retour address. Taken from originalDeliveryAddressIndex when the collo has it, otherwise
 * from the history. undefined when it can't be found, e.g. an old retour without colli in its history.
 */
export function getOriginalDeliveryAddressIndex(order: Pick<Order, 'history'>, collo: {index?: number, originalDeliveryAddressIndex?: number}): number | undefined {
    if (typeof collo.originalDeliveryAddressIndex === 'number') {
        return collo.originalDeliveryAddressIndex
    }

    if (typeof collo.index !== 'number') {
        return undefined
    }

    const deliveryAddressIndex = getColliBeforeRetour(order)[collo.index]?.deliveryAddressIndex

    return typeof deliveryAddressIndex === 'number' ? deliveryAddressIndex : undefined
}
